import { products } from '../data/products.js';
import { headwearLandingPages } from '../data/headwearLandingPages.js';
import { headwearContent } from '../data/headwearContent.js';
import { buyerLandingPages } from '../data/buyerLandingPages.js';

export const SITE_NAME = 'Printeam';
export const SITE_URL = 'https://www.printeam.co.il';
export const DEFAULT_SEO_TITLE = 'הדפסה על חולצות וביגוד בהתאמה אישית | Printeam';
export const DEFAULT_SEO_DESCRIPTION =
  'עיצוב והדפסה בהתאמה אישית על חולצות, קפוצ׳ונים, כובעים ומוצרי טקסטיל.';
export const DEFAULT_SOCIAL_IMAGE_PATH = '/printeam-social-card.webp';

const freezeRoute = (route) =>
  Object.freeze({
    robots: route.indexable ? 'index,follow' : 'noindex,nofollow',
    ...route,
  });

export const summarizeSeoDescription = (value, fallback) => {
  const normalized = String(value || fallback)
    .replace(/\s+/g, ' ')
    .trim();
  if (normalized.length <= 160) return normalized;

  const candidate = normalized.slice(0, 159);
  const lastWordBoundary = candidate.lastIndexOf(' ');
  const truncated =
    lastWordBoundary > 0 ? candidate.slice(0, lastWordBoundary) : candidate;
  return `${truncated.trimEnd()}…`;
};

const PRODUCT_SEO_COPY_OVERRIDES = Object.freeze({
  dryfit: {
    title: 'חולצת דרייפיט להדפסה ולמיתוג | Printeam',
    description: 'חולצת דרייפיט בעלת צווארון עגול, 100% פוליאסטר, לפעילות ולעבודה. בוחרים צבעים, מידות וכמויות ומוסיפים עיצוב באזורי המיתוג המוצגים בדף המוצר.',
  },
});

const PRODUCT_SEO_NAME_OVERRIDES = Object.freeze({
  hoodie: 'קפוצ׳ון',
  sweatshirt: 'סווטשירט',
  KR2210: 'כובע מצחיה מדגם KR2210',
  OM6157: 'כובע מצחיה מדגם OM6157',
  OM6500: 'כובע פטריה דו-שכבתי',
});

const headwearSeoDescription = (landing) => {
  const description = headwearContent[landing.key]?.description;
  if (!description) {
    throw new Error(`Missing reader-facing SEO description for headwear route "${landing.key}"`);
  }
  return summarizeSeoDescription(description, DEFAULT_SEO_DESCRIPTION);
};

export const normalizeRoutePath = (value = '/') => {
  const withoutQuery = String(value || '/').split(/[?#]/, 1)[0] || '/';
  const withLeadingSlash = withoutQuery.startsWith('/') ? withoutQuery : `/${withoutQuery}`;
  if (withLeadingSlash === '/') return '/';
  return withLeadingSlash.replace(/\/+$/, '');
};

export const canonicalUrl = (pathname = '/') => {
  const normalizedPath = normalizeRoutePath(pathname);
  return normalizedPath === '/' ? `${SITE_URL}/` : `${SITE_URL}${normalizedPath}`;
};

export const staticIndexableRoutes = Object.freeze(
  [
    {
      id: 'home',
      path: '/',
      title: DEFAULT_SEO_TITLE,
      description:
        'מעצבים ומזמינים ביגוד מודפס בהתאמה אישית: חולצות, קפוצ׳ונים, כובעים ומוצרים נוספים.',
      fallbackHeading: 'הדפסה על חולצות וביגוד בהתאמה אישית',
      preloadImage: '/hero_images/works_page/hero_18.webp',
    },
    {
      id: 'catalog',
      path: '/catalog',
      title: 'קטלוג ביגוד ומוצרים להדפסה | Printeam',
      description:
        'קטלוג מוצרי Printeam לעיצוב והדפסה בהתאמה אישית, עם בחירה לפי מוצר, צבע ומידה.',
      fallbackHeading: 'קטלוג מוצרים להדפסה',
    },
    {
      id: 'winter-campaign',
      path: '/winter-campaign',
      title: 'ביגוד חורף בעיצוב אישי | Printeam',
      description:
        'קפוצ׳ונים, סווטשירטים, פליס, סופטשל, חולצות ארוכות וכובעי חורף לעיצוב ומיתוג. בוחרים מוצר וממשיכים לצבעים, מידות ואפשרויות המיתוג בדף המוצר.',
      fallbackHeading: 'החורף שלכם. העיצוב שלכם.',
      preloadImage: '/landing/winter/hero.webp',
    },
    {
      id: 'faq',
      path: '/faq',
      title: 'שאלות נפוצות על הדפסה והזמנה | Printeam',
      description:
        'תשובות לשאלות נפוצות על עיצוב, הדפסה, הזמנה, משלוח והשימוש באתר Printeam.',
      fallbackHeading: 'שאלות נפוצות',
    },
    {
      id: 'print-quality',
      path: '/print-quality',
      title: 'איכות הדפסה ושיטות עבודה | Printeam',
      description: 'מידע על איכות ההדפסה, תהליכי העבודה והתאמת שיטת המיתוג למוצר.',
      fallbackHeading: 'איכות ההדפסה',
    },
    {
      id: 'garments-quality',
      path: '/garments-quality',
      title: 'איכות הביגוד, הבדים והגזרות | Printeam',
      description: 'מידע על איכות הביגוד, סוגי הבדים, הגזרות והטיפול במוצרים מודפסים.',
      fallbackHeading: 'איכות הביגוד',
    },
    {
      id: 'service-quality',
      path: '/service-quality',
      title: 'שירות ותהליך ההזמנה | Printeam',
      description: 'מידע על תהליך השירות, התקשורת והליווי בהזמנות דרך Printeam.',
      fallbackHeading: 'איכות השירות',
    },
    {
      id: 'about',
      path: '/about',
      title: 'אודות Printeam',
      description: 'מידע על Printeam ועל שירותי העיצוב וההדפסה המוצעים באתר.',
      fallbackHeading: 'אודות Printeam',
    },
    {
      id: 'terms',
      path: '/terms',
      title: 'תנאי שימוש | Printeam',
      description: 'תנאי השימוש וההזמנה באתר Printeam.',
      fallbackHeading: 'תנאי שימוש',
    },
    {
      id: 'privacy',
      path: '/privacy',
      title: 'מדיניות פרטיות | Printeam',
      description: 'מדיניות הפרטיות של אתר Printeam.',
      fallbackHeading: 'מדיניות פרטיות',
    },
    {
      id: 'returns',
      path: '/returns',
      title: 'מדיניות החזרות וביטולים | Printeam',
      description: 'מדיניות ההחזרות והביטולים להזמנות באתר Printeam.',
      fallbackHeading: 'מדיניות החזרות וביטולים',
    },
    {
      id: 'methods-of-branding',
      path: '/methods-of-branding',
      title: 'שיטות הדפסה ומיתוג על טקסטיל | Printeam',
      description: 'סקירת שיטות ההדפסה והמיתוג הזמינות והתאמתן למוצרים ולשימושים שונים.',
      fallbackHeading: 'שיטות הדפסה ומיתוג',
    },
    {
      id: 'accessibility',
      path: '/accessibility',
      title: 'הצהרת נגישות | Printeam',
      description: 'הצהרת הנגישות של אתר Printeam ודרכי יצירת קשר בנושא נגישות.',
      fallbackHeading: 'הצהרת נגישות',
    },
    {
      id: 'works',
      path: '/works',
      title: 'עבודות הדפסה ומיתוג | Printeam',
      description: 'דוגמאות לעבודות הדפסה ומיתוג שבוצעו ומוצגות באתר Printeam.',
      fallbackHeading: 'עבודות הדפסה ומיתוג',
    },
    {
      id: 'contact',
      path: '/contact',
      title: 'יצירת קשר | Printeam',
      description: 'יצירת קשר עם Printeam בנוגע למוצרים, עיצוב, כמויות והזמנות.',
      fallbackHeading: 'יצירת קשר',
    },
  ].map((route) => freezeRoute({ ...route, indexable: true, type: 'static' })),
);

export const headwearSeoRoutes = Object.freeze(
  headwearLandingPages.map((landing) =>
    freezeRoute({
      id: `headwear-${landing.key}`,
      type: 'headwear',
      path: landing.path,
      landingKey: landing.key,
      title: `${landing.navLabel} | Printeam`,
      description: headwearSeoDescription(landing),
      fallbackHeading: headwearContent[landing.key].title,
      indexable: true,
    }),
  ),
);

export const buyerSeoRoutes = Object.freeze(
  buyerLandingPages.map((landing) =>
    freezeRoute({
      id: `buyer-${landing.key}`,
      type: 'buyer',
      path: landing.path,
      landingKey: landing.key,
      title: landing.seoTitle,
      description: summarizeSeoDescription(landing.seoDescription, DEFAULT_SEO_DESCRIPTION),
      fallbackHeading: landing.title,
      indexable: true,
    }),
  ),
);

export const productSeoRoutes = Object.freeze(
  products.map((product) => {
    const productSeoName =
      PRODUCT_SEO_NAME_OVERRIDES[product.sku] || product.nameHe || product.name;
    const copy = PRODUCT_SEO_COPY_OVERRIDES[product.sku];

    return freezeRoute({
      id: `product-${product.sku}`,
      type: 'product',
      path: `/product/${product.sku}`,
      sku: product.sku,
      title: copy?.title || `${productSeoName} לעיצוב ומיתוג בהתאמה אישית | Printeam`,
      description: summarizeSeoDescription(
        copy?.description || `${productSeoName} לעיצוב ומיתוג בהתאמה אישית באתר Printeam. בדף המוצר אפשר לבחור מבין אפשרויות המוצר ואזורי המיתוג המוצגים בו.`,
        DEFAULT_SEO_DESCRIPTION,
      ),
      fallbackHeading: productSeoName,
      indexable: true,
    });
  }),
);

export const noindexRoutes = Object.freeze(
  [
    {
      id: 'cart',
      path: '/cart',
      title: 'סל הקניות | Printeam',
      description: 'סל הקניות האישי באתר Printeam.',
      fallbackHeading: 'סל הקניות',
      type: 'transactional',
    },
    {
      id: 'checkout-success',
      path: '/checkout/success',
      title: 'אישור הזמנה | Printeam',
      description: 'עמוד אישור הזמנה באתר Printeam.',
      fallbackHeading: 'אישור הזמנה',
      type: 'transactional',
    },
    {
      id: 'checkout-cancel',
      path: '/checkout/cancel',
      title: 'ההזמנה לא הושלמה | Printeam',
      description: 'עמוד חזרה מתהליך התשלום באתר Printeam.',
      fallbackHeading: 'ההזמנה לא הושלמה',
      type: 'transactional',
    },
    {
      id: 'thank-you',
      path: '/thank-you',
      title: 'תודה על ההזמנה | Printeam',
      description: 'עמוד אישור לאחר השלמת הזמנה באתר Printeam.',
      fallbackHeading: 'תודה על ההזמנה',
      type: 'transactional',
    },
    {
      id: 'thank-you-cc',
      path: '/thank-you-cc',
      title: 'אישור תשלום | Printeam',
      description: 'עמוד אישור לאחר תשלום באתר Printeam.',
      fallbackHeading: 'אישור תשלום',
      type: 'transactional',
    },
    {
      id: 'admin',
      path: '/admin',
      title: 'ניהול | Printeam',
      description: 'אזור ניהול של Printeam.',
      fallbackHeading: 'אזור ניהול',
      type: 'private',
    },
  ].map((route) => freezeRoute({ ...route, indexable: false })),
);

export const indexableRoutes = Object.freeze([
  ...staticIndexableRoutes,
  ...buyerSeoRoutes,
  ...headwearSeoRoutes,
  ...productSeoRoutes,
]);

export const productionRoutes = Object.freeze([...indexableRoutes, ...noindexRoutes]);

export const notFoundRoute = freezeRoute({
  id: 'not-found',
  type: 'error',
  path: '/404',
  title: 'העמוד לא נמצא | Printeam',
  description: 'העמוד המבוקש אינו קיים. אפשר לחזור לעמוד הבית או לעבור לקטלוג.',
  fallbackHeading: 'העמוד לא נמצא',
  indexable: false,
});

const routeByPath = new Map(productionRoutes.map((route) => [route.path, route]));
const productSkuSet = new Set(productSeoRoutes.map((route) => route.sku));

export const getSeoRoute = (pathname) => routeByPath.get(normalizeRoutePath(pathname)) || null;

export const isKnownProductSku = (sku) => productSkuSet.has(String(sku || ''));
